<template>
    <div class="mf-loader">
        <div v-if="loading">
            <slot name="loading">Loading MF...</slot>
        </div>
        <iframe
            v-show="!loading"
            ref="iframeRef"
            :src="computedMfUrl"
            frameborder="0"
            :class="{
                'with-loading-params': iframeLoadingEvent,
            }"
            @load="iframeLoadingEvent && handleIframeLoading()"
        />
    </div>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, onBeforeUnmount, onMounted, onUnmounted, computed} from 'vue';
    import {iframeResizer} from 'iframe-resizer';
    import {useGuestHost} from '#imports';

    const host = useGuestHost();

    interface Props {
        mfUrl: string;
        mfArgs?: Record<string, any>;
        iframeAutoResize?: boolean;
        iframeLoadingEvent?: boolean;
    }

    interface MFEventData {
        from: string;
        payload: any;
    }

    interface ResizableHTMLIFrameElement extends HTMLIFrameElement {
        iFrameResizer?: {
            removeListeners: () => void;
        };
    }

    const props = defineProps<Props>();

    const emit = defineEmits<{
        mounted: [];
        message: [data: any];
    }>();

    const computedMfUrl = computed(() => {
        const url = new URL(props.mfUrl, host);
        url.searchParams.append('app-type', 'microfrontend');
        url.searchParams.sort();

        return `${url.pathname}${url.search}`;
    });

    const iframeRef = useTemplateRef('iframeRef');
    const loading = ref(true);

    function handleIframeLoading() {
        loading.value = false;
    }

    function isMFEventData(eventData: unknown): eventData is MFEventData {
        return (eventData as MFEventData).from !== undefined;
    }

    function microFrontendLoaded() {
        loading.value = false;

        if (props.mfArgs) {
            postMessage(props.mfArgs);
        }

        emit('mounted');

        if (props.iframeAutoResize && iframeRef.value) {
            iframeResizer({}, iframeRef.value);
        }
    }

    function handleMicroFrontendEvent(event: MessageEvent) {
        if (event.origin !== window.location.origin) {
            return;
        }
        if (!isMFEventData(event.data)) {
            return;
        }
        const {from, payload} = event.data;
        if (from !== computedMfUrl.value) {
            return;
        }
        if (payload === 'mf:mounted') {
            microFrontendLoaded();
            return;
        }
        emit('message', event.data.payload);
    }

    onMounted(() => {
        window.addEventListener('message', handleMicroFrontendEvent);
    });

    onBeforeUnmount(() => {
        const iframe = iframeRef.value as ResizableHTMLIFrameElement | null;
        if (iframe?.iFrameResizer) {
            iframe.iFrameResizer.removeListeners();
        }
    });

    onUnmounted(() => {
        window.removeEventListener('message', handleMicroFrontendEvent);
    });

    function postMessage<MessageType>(message: MessageType) {
        iframeRef.value!.contentWindow!.postMessage(message, window.location.origin);
    }

    defineExpose({
        postMessage,
    });
</script>

<style scoped lang="scss">
    .mf-loader {
        > iframe {
            &.with-loading-params {
                min-height: 800px;
            }

            width: 100%;
            height: 100%;
            border: none;
        }
    }
</style>
